import React, { createContext, useCallback, useEffect, useRef } from 'react';

import { usePathname } from 'next/navigation';

import { useTrackPublisherEvents } from '@/api/useTrackPublisherEvents';
import { EEventsType, EPlatformType, EPublisherEvents, EPublisherWebStoreEventType } from '@/lib/enums';
import { enrichOptions } from '@/lib/eventDataUtils';
import { trackMixpanelEvent } from '@/lib/mixpanelClient';
import { useAppState } from '@/state/hooks/app.state.hook';
import { popupStateService } from '@/state/popup.state.service';
import type { ProductData } from '@appcharge/shared-ui';
import { arrangeMixpanelOfferData } from './EventBatchProvider.utils';
import { EPopupSubType } from '@/lib/enums';

// Generic event types
interface BaseEventData {
  id: string;
  type: string;
  timestamp?: number;
}

interface OfferEventData extends BaseEventData {
  offerId: string;
  offerName: string;
  price?: number;
  publisherOfferId?: string;
  offerType?: string;
  indexToCollect?: number;
  subType?: string;
  priority?: number;
  isAllowedToCollect?: boolean;
  displayRule?: { frequency?: number; cooldownInMinutes?: number };
  productSale?: { sale: number };
  schedule?: { timeFrames: Array<{ startTime: Date | string; endTime: Date | string }> };
  rewardCalendar?: { unlockedIndex: number; nextUnlockAt: Date };
  productsSequence?: Array<{
    products: ProductData[];
    prices: { priceInUsdCents: number };
  }>;
}

interface GameEventData extends BaseEventData {
  gameId: string;
  gameName: string;
  category?: string;
  platform?: string;
}

interface BannerEventData extends BaseEventData {
  bannerId: string;
  bannerType: string;
  campaignId?: string;
}

// Union type for all event types
type EventData = OfferEventData | GameEventData | BannerEventData;

// Generic context type
export interface EventContextType {
  sendEvent: (event: EventData, identifier?: string) => void;
  resetObservedItems: () => void;
  setEventType: (eventType: string) => void;
}

const observedItems = [] as string[];

const isPopupDisplayed = () => Boolean(popupStateService.currentPopupDisplayed.get());

export const EventBatchContext = createContext<EventContextType | undefined>(undefined);

export const EventBatchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const eventsRef = useRef<EventData[]>([]);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const currentEventTypeRef = useRef<string>(EEventsType.OFFERS_SHOWN);
  const debounceDelay = 1000;
  const appState = useAppState();
  const {
    platform,
    currentPageName
  }: { platform: EPlatformType | undefined; currentPageName: string | undefined } = useAppState();
  const playerData = appState.playerData;
  const pathname = usePathname();
  const { trackPublisherUIEvent, trackPublisherWebStoreEvent } = useTrackPublisherEvents();

  const arrangeEventData = (data: EventData[], eventType: string) => {
    switch (eventType) {
      case EEventsType.OFFERS_SHOWN:
        return { offers: arrangeMixpanelOfferData(data as OfferEventData[]) };
      case EEventsType.PORTAL_CONTENT_DISPLAYED:
        return {
          content: data.map((item: any) => ({
            id: item.id || '',
            type: item.type || '',
            index: item.index || 0,
            title: item.title || '',
            slug: item.slug || ''
          })),
          'sessionMetaData.pageUrl': pathname,
          'sessionMetaData.pageName': currentPageName,
          'sessionMetaData.appVersion': process.env.NEXT_PUBLIC_APP_VERSION,
          player_id: playerData?.playerId
        };
      default:
        return data;
    }
  };

  const extractOfferMainDetails = (offers: OfferEventData[]) => {
    return offers.map((offer: any) => {
      const activeSequence =
        offer.productsSequence?.[offer.indexToCollect ? offer.indexToCollect - 1 : 0];

      return {
        id: offer.offerId,
        name: offer.offerName,
        publisherOfferId: offer.publisherOfferId || offer.offerId,
        type: offer.offerType,
        index: offer.indexToCollect,
        pricePoint: activeSequence?.prices?.priceInUsdCents / 100,
        subType: offer.subType,
        offerJwt: offer.offerJwt,
        products:
          activeSequence?.products?.map((product: any) => ({
            publisherProductId: product.publisherProductId,
            amount: product.quantity
          })) ?? []
      };
    });
  };

  const flushEvents = () => {
    if (eventsRef.current.length === 0) return;

    const arrangedData = arrangeEventData(eventsRef.current, currentEventTypeRef.current);

    trackMixpanelEvent(currentEventTypeRef.current, {
      ...arrangedData,
      device_platform: platform
    });

    const publisherEventName =
      currentEventTypeRef.current === EEventsType.OFFERS_SHOWN
        ? EPublisherEvents.WEBSTORE_OFFERS_DISPLAYED
        : currentEventTypeRef.current;

    if (publisherEventName === EPublisherEvents.WEBSTORE_OFFERS_DISPLAYED) {
      trackPublisherWebStoreEvent.mutate({
        eventType: EPublisherWebStoreEventType.OFFERS,
        data: {
          ...enrichOptions({
            platform,
            eventName: publisherEventName,
            offers: extractOfferMainDetails(eventsRef.current as OfferEventData[]),
            pageUrl: pathname,
            pageName: currentPageName,
            playerId: playerData?.playerId
          })
        }
      });
    } else {
      trackPublisherUIEvent.mutate({
        ...enrichOptions({
          platform,
          eventName: EPublisherEvents[publisherEventName as keyof typeof EPublisherEvents],
          content: eventsRef.current,
          page: [
            {
              pageUrl: pathname,
              pageName: currentPageName
            }
          ],
          playerId: playerData?.playerId
        })
      });
    }

    eventsRef.current = [];
  };

  const isHeldByPopup = () =>
    currentEventTypeRef.current === EEventsType.OFFERS_SHOWN && isPopupDisplayed();

  const scheduleFlush = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (isHeldByPopup()) return;

    debounceTimerRef.current = setTimeout(() => {
      if (isHeldByPopup()) return;

      flushEvents();
    }, debounceDelay);
  };

  const resetObservedItems = () => {
    observedItems.length = 0;
  };

  const setEventType = (eventType: string) => {
    currentEventTypeRef.current = eventType;
  };

  useEffect(() => {
    resetObservedItems();
    if (isHeldByPopup()) eventsRef.current = [];
  }, [pathname]);

  const scheduleFlushRef = useRef(scheduleFlush);
  scheduleFlushRef.current = scheduleFlush;

  const sendEvent = useCallback((data: EventData, identifier?: string) => {
    if ((data as OfferEventData)?.subType === EPopupSubType.REWARD_CALENDAR) return;
    if (identifier && observedItems.includes(identifier)) return;

    if (identifier) observedItems.push(identifier);
    eventsRef.current.push(data);

    scheduleFlushRef.current();
  }, []);

  useEffect(() => {
    return popupStateService.currentPopupDisplayed.subscribe((popup) => {
      if (!popup) scheduleFlushRef.current();
    });
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <EventBatchContext.Provider
      value={{
        sendEvent,
        resetObservedItems,
        setEventType
      }}>
      {children}
    </EventBatchContext.Provider>
  );
};
